import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { TariffDirection } from '../../../api/Optimise';
import type { ChartDataItem } from '../../../types/charts';
import { addRateCategoriesToRecords, extractRates, RateCategories } from '../../../utils/savingsUtils';

const Container = styled.div`
  margin-top: 24px;
  border-radius: 8px;
  padding: 16px;
  background-image: ${(props) => props.theme.standardLayout.savings.tariffKeyTable.backgroundImage};
  background-color: ${(props) => props.theme.standardLayout.savings.tariffKeyTable.backgroundColor};
`;

const Title = styled.h3`
  color: ${(props) => props.theme.colors.white};
  text-transform: uppercase;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: 0;
  margin: 0 0 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${(props) => props.theme.colors.white};
`;

const HeaderRow = styled.tr`
  background: ${(props) => props.theme.colors.extraLight};
`;

const TariffDetailRow = styled.tr`
  background: ${(props) => props.theme.colors.white};
`;

const Th = styled.th<{ $color?: string }>`
  width: 33.333%;
  padding: 16px;
  font-size: 16px;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
  color: ${(props) => props.$color ?? 'transparent'};
  border-bottom: 1px solid ${(props) => props.theme.colors.light};
`;

const TariffDetailTd = styled.td<{ $center?: boolean; $bold?: boolean }>`
  width: 33.333%;
  padding: 16px;
  font-size: 16px;
  font-weight: ${(props) => (props.$bold ? 700 : 400)};
  line-height: 26px;
  text-align: ${(props) => (props.$center ? 'center' : 'left')};
  color: ${(props) => props.theme.colors.dark};
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.colors.light};
`;

const Td = styled.td<{ $center?: boolean }>`
  width: 33.333%;
  padding: 16px;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  text-align: ${(props) => (props.$center ? 'center' : 'left')};
  border-bottom: 1px solid ${(props) => props.theme.colors.light};
  vertical-align: middle;
`;

const RatePill = styled.span<{ $bg: string; $color: string; $border: string }>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  border-radius: 4px;
  background: ${(props) => props.$bg};
  border: 1px solid ${(props) => props.$border};
  color: ${(props) => props.$color};
  padding: 10px;
  font-size: 14px;
  font-weight: 700;
  line-height: 22px;
  text-transform: uppercase;
  text-align: center;
`;

interface TariffPanelProps {
  chartData: ChartDataItem[] | null | undefined;
  tariffNames: { export: string; import: string } | null;
}

const formatRate = (rate: number | null): string => {
  if (rate === null) return '—';
  return `${(rate * 100).toFixed(2)}p/kWh`;
};

const ROW_ORDER = [RateCategories.DAY, RateCategories.CHEAP, RateCategories.PEAK] as const;

const TariffPanel = ({ chartData, tariffNames }: TariffPanelProps) => {
  const theme = useTheme();
  const [rates, setRates] = useState<ReturnType<typeof extractRates> | null>(null);

  useEffect(() => {
    if (chartData?.length) {
      const withImport = addRateCategoriesToRecords(
        chartData as Parameters<typeof addRateCategoriesToRecords>[0],
        TariffDirection.IMPORT
      );
      const withBoth = addRateCategoriesToRecords(
        withImport as Parameters<typeof addRateCategoriesToRecords>[0],
        TariffDirection.EXPORT
      );
      setRates(extractRates(withBoth as Parameters<typeof extractRates>[0]));
    }
  }, [chartData]);

  const rowMeta: Record<string, { label: string; bg: string; color: string; border: string }> = {
    [RateCategories.DAY]: {
      label: 'Day Rate',
      bg: theme.colors.white,
      color: theme.colors.dark,
      border: theme.colors.black,
    },
    [RateCategories.CHEAP]: {
      label: 'Cheap Rate',
      bg: `linear-gradient(to bottom, ${theme.colors.success}, ${theme.colors.successDark})`,
      color: theme.colors.white,
      border: 'transparent',
    },
    [RateCategories.PEAK]: {
      label: 'Peak Rate',
      bg: `linear-gradient(to bottom, ${theme.colors.solarLight}, ${theme.colors.solar})`,
      color: theme.colors.dark,
      border: 'transparent',
    },
  };

  return (
    <Container>
      <Title>Your Tariff Rates Explained</Title>
      <Table>
        <thead>
          <HeaderRow>
            <Th />
            <Th $color={theme.colors.exported}>Export</Th>
            <Th $color={theme.colors.imported}>Import</Th>
          </HeaderRow>
        </thead>
        <tbody>
          <TariffDetailRow>
            <TariffDetailTd $bold>Tariff</TariffDetailTd>
            <TariffDetailTd $center>{tariffNames?.export ?? '—'}</TariffDetailTd>
            <TariffDetailTd $center>{tariffNames?.import ?? '—'}</TariffDetailTd>
          </TariffDetailRow>
          {ROW_ORDER.map((category) => {
            const meta = rowMeta[category];
            const exportRate = rates?.export?.[category]?.minRate ?? null;
            const importRate = rates?.import?.[category]?.minRate ?? null;
            if (exportRate === null && importRate === null) return null;
            return (
              <tr key={category}>
                <Td>
                  <RatePill $bg={meta.bg} $color={meta.color} $border={meta.border}>
                    {meta.label}
                  </RatePill>
                </Td>
                <Td $center>{formatRate(exportRate)}</Td>
                <Td $center>{formatRate(importRate)}</Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default TariffPanel;
